"use strict";
/**
 * Demo-only helper that appends a "Use Case Metrics" column to the alignment
 * graph. The column is built purely on the frontend from the current use
 * case's metrics and is never persisted. Connections and weights are
 * pseudo-random but seeded from node ids/names, so the graph stays stable
 * between renders.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.collectMetricPathNodeIds = exports.appendUseCaseColumn = exports.appendUseCaseMetricsColumn = exports.APPENDED_COLUMN_IDS = exports.USE_CASE_COLUMN_NAME = exports.USE_CASE_COLUMN_ID = exports.USE_CASE_METRICS_COLUMN_NAME = exports.USE_CASE_METRICS_COLUMN_ID = void 0;
var constants_1 = require("../../shared/constants");
exports.USE_CASE_METRICS_COLUMN_ID = 'use-case-metrics';
exports.USE_CASE_METRICS_COLUMN_NAME = 'Use Case Metrics';
exports.USE_CASE_COLUMN_ID = 'use-case';
exports.USE_CASE_COLUMN_NAME = 'Use Case';
/** Columns added on the frontend, i.e. not part of the strategy itself. */
exports.APPENDED_COLUMN_IDS = new Set([
    exports.USE_CASE_METRICS_COLUMN_ID,
    exports.USE_CASE_COLUMN_ID,
]);
var EDGE_WEIGHTS = ['low', 'medium', 'high', 'very-high'];
/** Target share of metrics left without any connection from the strategy. */
var UNLINKED_METRIC_RATIO = 0.3;
/** Chance that a metric gets a second, extra connection. */
var EXTRA_LINK_CHANCE = 0.1;
var hashString = function (value) {
    var hash = 2166136261;
    for (var index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
};
/** Mulberry32 – small deterministic PRNG returning values in [0, 1). */
var createSeededRandom = function (seed) {
    var state = seed;
    return function () {
        state = (state + 0x6d2b0f6) >>> 0;
        var result = Math.imul(state ^ (state >>> 15), 1 | state);
        result = (result + Math.imul(result ^ (result >>> 7), 61 | result)) ^ result;
        return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
    };
};
/**
 * Return a copy of `structure` with an extra right-most "Use Case Metrics"
 * column and random edges from the previous last column into it.
 */
exports.appendUseCaseMetricsColumn = function (structure, metricNames) {
    if (metricNames.length === 0) {
        return structure;
    }
    var metricNodes = metricNames.map(function (name, index) { return ({
        id: exports.USE_CASE_METRICS_COLUMN_ID + "-" + index,
        name: name,
        edges: {},
    }); });
    var metricsColumn = {
        id: exports.USE_CASE_METRICS_COLUMN_ID,
        name: exports.USE_CASE_METRICS_COLUMN_NAME,
        nodes: metricNodes,
    };
    if (structure.length === 0) {
        return [metricsColumn];
    }
    var lastColumnIndex = structure.length - 1;
    var lastColumn = structure[lastColumnIndex];
    if (lastColumn.nodes.length === 0) {
        return __spreadArrays(structure, [metricsColumn]);
    }
    var random = createSeededRandom(hashString(__spreadArrays(lastColumn.nodes.map(function (node) { return node.id; }), metricNames).join('|')));
    var pickRandom = function (items) { return items[Math.floor(random() * items.length)]; };
    // Exactly half of the source nodes (rounded up) get connections; the rest
    // stay as empty paths.
    var shuffledSourceIds = lastColumn.nodes
        .map(function (node) { return ({ id: node.id, order: random() }); })
        .sort(function (left, right) { return left.order - right.order; })
        .map(function (entry) { return entry.id; });
    var linkedMetricCount = Math.max(1, Math.round(metricNodes.length * (1 - UNLINKED_METRIC_RATIO)));
    var activeSourceCount = Math.ceil(shuffledSourceIds.length / 2);
    var activeSourceIds = shuffledSourceIds.slice(0, activeSourceCount);
    var linkedMetricNodes = metricNodes
        .map(function (node) { return ({ node: node, order: random() }); })
        .sort(function (left, right) { return left.order - right.order; })
        .slice(0, linkedMetricCount)
        .map(function (entry) { return entry.node; });
    var edgesBySourceId = new Map();
    var addEdge = function (sourceId, metricId) {
        var _a;
        var sourceEdges = (_a = edgesBySourceId.get(sourceId)) !== null && _a !== void 0 ? _a : {};
        sourceEdges[metricId] = pickRandom(EDGE_WEIGHTS);
        edgesBySourceId.set(sourceId, sourceEdges);
    };
    // Every linked metric gets one source; the first pass also gives each active
    // source at least one metric so no chosen source ends up empty.
    linkedMetricNodes.forEach(function (metricNode, metricIndex) {
        var sourceId = metricIndex < activeSourceIds.length
            ? activeSourceIds[metricIndex]
            : pickRandom(activeSourceIds);
        addEdge(sourceId, metricNode.id);
        if (activeSourceIds.length > 1 && random() < EXTRA_LINK_CHANCE) {
            addEdge(pickRandom(activeSourceIds), metricNode.id);
        }
    });
    // Covers the case of fewer linked metrics than active sources.
    activeSourceIds
        .filter(function (sourceId) { return !edgesBySourceId.has(sourceId); })
        .forEach(function (sourceId) { return addEdge(sourceId, pickRandom(linkedMetricNodes).id); });
    var linkedLastColumn = __assign(__assign({}, lastColumn), { nodes: lastColumn.nodes.map(function (node) {
            var randomEdges = edgesBySourceId.get(node.id);
            return randomEdges ? __assign(__assign({}, node), { edges: __assign(__assign({}, node.edges), randomEdges) }) : node;
        }) });
    return __spreadArrays(structure.slice(0, lastColumnIndex), [linkedLastColumn, metricsColumn]);
};
/**
 * Return a copy of `structure` with an extra right-most "Use Case" column
 * holding a single node for the current use case, linked from every node in
 * the "Use Case Metrics" column.
 */
exports.appendUseCaseColumn = function (structure, useCaseTitle) {
    var trimmedTitle = useCaseTitle.trim();
    if (trimmedTitle.length === 0) {
        return structure;
    }
    var useCaseNodeId = exports.USE_CASE_COLUMN_ID + "-0";
    var useCaseColumn = {
        id: exports.USE_CASE_COLUMN_ID,
        name: exports.USE_CASE_COLUMN_NAME,
        nodes: [{ id: useCaseNodeId, name: trimmedTitle, edges: {} }],
    };
    var linkedStructure = structure.map(function (column) {
        return column.id === exports.USE_CASE_METRICS_COLUMN_ID
            ? __assign(__assign({}, column), { nodes: column.nodes.map(function (node) {
                    var _a;
                    return (__assign(__assign({}, node), { edges: __assign(__assign({}, node.edges), (_a = {}, _a[useCaseNodeId] = constants_1.DEFAULT_EDGE_WEIGHT, _a)) }));
                }) }) : column;
    });
    return __spreadArrays(linkedStructure, [useCaseColumn]);
};
/**
 * Collect ids of the use case metric nodes and every node upstream of them,
 * i.e. all nodes lying on a path that ends in a use case metric.
 */
exports.collectMetricPathNodeIds = function (structure) {
    var metricPathNodeIds = new Set(structure
        .filter(function (column) { return column.id === exports.USE_CASE_METRICS_COLUMN_ID || column.id === exports.USE_CASE_COLUMN_ID; })
        .flatMap(function (column) { return column.nodes.map(function (node) { return node.id; }); }));
    var allNodes = structure.flatMap(function (column) { return column.nodes; });
    var hasNewNodes = metricPathNodeIds.size > 0;
    while (hasNewNodes) {
        hasNewNodes = false;
        allNodes.forEach(function (node) {
            var leadsToMetric = Object.keys(node.edges || {}).some(function (targetId) {
                return metricPathNodeIds.has(targetId);
            });
            if (leadsToMetric && !metricPathNodeIds.has(node.id)) {
                metricPathNodeIds.add(node.id);
                hasNewNodes = true;
            }
        });
    }
    return metricPathNodeIds;
};
